import CustomText from "@components/CustomText";
import { colors } from "@constants/theme";
import React, { useState } from "react";
import Cross from "@assets/svg/CrossIcon";
import InfoCircle from "@assets/svg/InfoCircle";
import {
  StyleSheet,
  TouchableOpacity,
  View,
  Modal,
  TextInput,
  ScrollView,
  TouchableWithoutFeedback,
} from "react-native";
import { KeyboardStickyView } from "react-native-keyboard-controller";
import t from "@constants/localization";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import BottomButton from "./BottomButton";

/** BottomButton renders position:absolute / bottom:0 / elevation:10 inside
 *  contentContainer, so it is out of flow and this ScrollView has to reserve its
 *  height by hand. Real footer height = 16 (absoluteContainer paddingTop)
 *  + 10 (spacer marginBottom) + 20 (scanButton paddingVertical)
 *  + 24 * lines (title_16 lineHeight) + 10 (paddingBottom) + insets.bottom.
 *  The Hindi CTA "बिना बैग नंबर के आगे बढ़ें" wraps to two lines, so the worst
 *  case is 104, not 60 — at 60 the white footer painted over the last 44dp of
 *  the ScrollView, eating the bottom 20dp of the "नया बैग" button. */
const FOOTER_RESERVE = 104;

const AddBagNoModal = ({
  visible,
  onClose,
  onSave,
  item,
  error,
  btnLoading,
}) => {
  const insets = useSafeAreaInsets();
  const [bagNumbers, setBagNumbers] = useState(item?.bags || [""]);
  const [focusedIndex, setFocusedIndex] = useState(null);

  const PATH =
    "screens/MyDeliveries/SortingDeliveries/components/AddBagNoModal";

  const handleAddBag = () => {
    setBagNumbers([...bagNumbers, ""]);
  };

  const handleChangeBag = (index, value) => {
    const newBagNumbers = [...bagNumbers];
    newBagNumbers[index] = value;
    setBagNumbers(newBagNumbers);
  };

  const closeModel = () => {
    setBagNumbers(item?.bags || [""]);
    setFocusedIndex(null);
    onClose();
  };

  return (
    <Modal
      statusBarTranslucent
      navigationBarTranslucent
      animationType="fade"
      transparent={true}
      visible={visible}
      onRequestClose={closeModel}
    >
      <TouchableWithoutFeedback onPress={closeModel}>
        <View style={styles.modalBackground} />
      </TouchableWithoutFeedback>
      <KeyboardStickyView style={styles.contentContainer}>
        <View style={styles.modalCross}>
          <TouchableWithoutFeedback onPress={closeModel}>
            <Cross width={16} height={16} />
          </TouchableWithoutFeedback>
        </View>
        <View style={styles.headingContainer}>
          <View>
            <CustomText variant="title_20">{t(`${PATH}/addBagNo`)}</CustomText>
            <CustomText
              variant="body_text_2"
              style={{ color: colors.mediumBlacklightBlack }}
            >
              {`${item?.route_name} - ${item?.db_name || "Unassigned"} `}
            </CustomText>
          </View>
        </View>
        <ScrollView
          style={[
            styles.sectionContainer,
            { marginBottom: FOOTER_RESERVE + insets.bottom },
          ]}
          keyboardShouldPersistTaps="handled"
        >
          {bagNumbers.map((bagNumber, index) => (
            <TextInput
              testID="sd-bag-no"
              style={[
                styles.textInput,
                focusedIndex === index && styles.textInputFocused,
              ]}
              value={bagNumber.toString() || ""}
              onChangeText={(text) => handleChangeBag(index, text)}
              placeholderTextColor="rgba(0, 0, 0, 0.20)"
              placeholder={t(`${PATH}/placeholder`)}
              onFocus={() => setFocusedIndex(index)}
              onBlur={() => setFocusedIndex(null)}
              autoFocus={index === bagNumbers.length - 1}
              keyboardType="number-pad"
            />
          ))}
          {error.length > 0 && (
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                borderColor: colors.lighterBlack,
                borderWidth: 1,
                borderRadius: 4,
                padding: 8,
                marginHorizontal: 8,
              }}
            >
              <InfoCircle />
              <CustomText
                variant="caption_2"
                style={{ color: colors.mediumBlack, marginLeft: 8 }}
              >
                {error}
              </CustomText>
            </View>
          )}
          <TouchableOpacity
            testID="sd-add-bag"
            onPress={handleAddBag}
            style={styles.addButton}
          >
            <CustomText variant="subtitle_16" style={styles.addButtonText}>
              {t(`${PATH}/newBag`)}
            </CustomText>
          </TouchableOpacity>
        </ScrollView>
        <BottomButton
          onPress={() => {
            onSave(bagNumbers.every((bag) => bag === "") ? [] : bagNumbers);
            setFocusedIndex(null);
          }}
          title={
            bagNumbers.every((bag) => bag === "")
              ? t(`${PATH}/saveWithoutBag`)
              : t(`${PATH}/save`)
          }
          loading={btnLoading}
          bottomInset={insets.bottom}
        />
      </KeyboardStickyView>
    </Modal>
  );
};

export default AddBagNoModal;

const styles = StyleSheet.create({
  sectionContainer: {
    padding: 12,
    paddingTop: 0,
    paddingLeft: 16,
    marginBottom: FOOTER_RESERVE,
    maxHeight: "70%",
  },

  modalBackground: {
    flex: 1,
    backgroundColor: colors.mediumBlacklightBlack,
    justifyContent: "flex-end",
    alignItems: "center",
  },
  contentContainer: {
    backgroundColor: "#fff",
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    width: "100%",
    position: "absolute",
    bottom: 0,
  },

  headingContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    padding: 16,
    paddingTop: 20,
  },

  textInput: {
    borderWidth: 1,
    borderColor: "black",
    padding: 8,
    paddingLeft: 16,
    minHeight: 80,
    fontSize: 32,
    fontWeight: "bold",
    margin: 8,
    borderRadius: 2,
  },

  textInputFocused: {
    borderColor: colors.primary,
  },

  addButton: {
    width: "30%",
    margin: 8,
    borderColor: colors.plain_red,
    borderWidth: 1,
    borderRadius: 8,
    alignItems: "center",
    marginBottom: 12,
  },

  addButtonText: {
    color: colors.plain_red,
    padding: 8,
  },
  modalCross: {
    position: "absolute",
    right: 10,
    top: -35,
    backgroundColor: "#fff",
    borderRadius: 50,
    zIndex: 1,
    padding: 6,
  },
});
